import type { AssessmentTemplate } from '../../model/assessments/interfaces'
import type { AssessmentTemplatePayload } from '../../model/assessments/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { toAssessmentTemplate } from './template-response'

interface AssessmentTemplateStoreApiOptions {
  /** The **working document** payload — never Zod's parsed output (decision B9). */
  payload: AssessmentTemplatePayload
  onStart?: () => void
  onSuccess?: (template: AssessmentTemplate) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/**
 * `POST /admin-api/assessment-templates` — store (contract §3). The `201` body is normalised
 * through the same `toAssessmentTemplate` as `show`: without it, the second save of a freshly
 * created template re-submits `rubrics: []` where a map belongs (B7).
 */
export const useAssessmentTemplateStoreApi = async (
  options: AssessmentTemplateStoreApiOptions,
): Promise<AssessmentTemplate | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assessment-templates.store')

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, options.payload)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const template = toAssessmentTemplate(data.data)

    if (!template) {
      const msg = 'Unexpected error. The assessment template is missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    toast.success('Assessment template created')
    options.onSuccess?.(template)
    return template
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
